import React, { useEffect, useRef, useState } from "react";
import ProductGrid from "./ProductGrid";
import { useSearchParams } from "react-router-dom";
import LoadMoreButton from "../../../shared/ui/LoadMoreButton";
import NoResults from "../../../shared/ui/NoResults";
import ProductGridSkeleton from "./ProductGridSkeleton";
import { searchProductsWithKeywords } from "../api";
import { mergeProducts } from "../utils/productDisplay";

// showLoadMore: a "Load More Products" link to /products (home page);
// otherwise a "Load more" button that appends the next page in place.
const ProductList = ({ pageSize, showLoadMore = true, bare = false }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";
  // Responses for an older search are ignored.
  const latestRequest = useRef(0);

  const fetchProducts = async (page) => {
    const request = ++latestRequest.current;
    setLoading(true);
    try {
      const data = await searchProductsWithKeywords(searchQuery, page, pageSize);
      if (request !== latestRequest.current) return;
      setTotalPages(data.totalPages);
      setProducts((prev) => mergeProducts(prev, data.content, page));
    } catch (error) {
      console.error("Error searching products:", error);
    } finally {
      if (request === latestRequest.current) setLoading(false);
    }
  };

  // A new search starts again from the first page.
  useEffect(() => {
    setProducts([]);
    setCurrentPage(0);
    fetchProducts(0);
  }, [searchQuery, pageSize]);

  const loadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts(nextPage);
  };

  if (loading && products.length === 0) {
    return <ProductGridSkeleton count={pageSize || 12} bare={bare} />;
  }
  if (products.length === 0) {
    return <NoResults message="No products found" />;
  }

  return (
    <>
      <ProductGrid
        products={products}
        currentPage={currentPage}
        totalPages={totalPages}
        showLoadmore={showLoadMore}
        bare={bare}
      />
      {!showLoadMore && (
        <LoadMoreButton
          hasMore={currentPage < totalPages - 1}
          loading={loading}
          onClick={loadMore}
        />
      )}
    </>
  );
};

export default ProductList;
